import { togglePlanoProduct } from "@/components/planogram/planoProductClickBridge";
import { PlanogramProductHoverContent } from "@/components/planogram/PlanogramProductHoverContent";
import {
  PlanogramProduct,
  type PlanogramProductRenderer,
  type PlanogramRenderedProduct,
} from "@augmodo/reusable-components";
import { HoverCard } from "@mantine/core";

interface Props {
  item: PlanogramRenderedProduct;
}

/**
 * One product cell of the reusable planogram viewer for the OOS confirmation
 * task: the viewer's default rendering (red outline when active) with clicks
 * routed into the useActivePlanoProductInfoStore bridge instead of the
 * built-in popover — the task shows product details in its own panel
 * (PlanogramProduct / the selected-items list), matching the legacy viewer's
 * `hidePopover` mode. Hovering shows an enlarged product card, the reusable
 * counterpart of the legacy OneLabelBox tooltip. The card renders through
 * Mantine with `withinPortal`: the cell lives inside the viewer's SVG, and
 * the portal is what lets the dropdown be HTML.
 */
export const ConfirmationPlanogramProduct = ({ item }: Props) => (
  <HoverCard
    position="right-start"
    offset={8}
    withinPortal
    shadow="md"
    // Same 500ms the legacy OneLabelBox tooltip waited before showing.
    openDelay={500}
    closeDelay={0}
    middlewares={{ flip: true, shift: true }}
    // Purely informational — never intercept the mouse on its way to
    // neighbouring products.
    styles={{ dropdown: { pointerEvents: "none" } }}
  >
    <HoverCard.Target>
      <PlanogramProduct
        {...item}
        // Suppress the built-in SVG <title>: the hover card replaces it, and
        // the native browser tooltip would double up on top of it.
        label=""
        style={{ cursor: "pointer" }}
        onClick={() => togglePlanoProduct(item)}
      />
    </HoverCard.Target>
    <HoverCard.Dropdown p={8}>
      <PlanogramProductHoverContent item={item} />
    </HoverCard.Dropdown>
  </HoverCard>
);

/**
 * The render prop handed to the reusable viewer. Module-level so its identity
 * never changes: the viewer's per-cell memo compares `children` by reference.
 */
export const renderConfirmationPlanogramProduct: PlanogramProductRenderer = (
  item,
) => <ConfirmationPlanogramProduct item={item} />;
